/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Divider, Grid, Typography } from '@mui/material';
import { DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange, environment } from '../../../shared/environment';
import { CompanyDTO, CompanyFilterDTO, ResumeDTO, ResumeFilterDTO, ProductDTO, ProductFilterDTO, UserDTO, ValueListDTO, UserFilterDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';
import { useTranslation } from 'react-i18next';

interface ICustomResumeProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const CustomResume: React.FC<ICustomResumeProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {

    const [jobTitleList, setJobTitleList] = useState<ValueListDTO[]>([]);
    const [seniorityList, setSeniorityList] = useState<ValueListDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [resumeDTO, setResumeDTO] = useState<ResumeDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const env = environment();
    const { t } = useTranslation();

    useEffect(() => {
        setResumeDTO({
            ...resumeDTO,
            id: '',
            name: '',
            jobTitleId: '',
            seniorityId: '',
            email: '',
            cellPhone: '',
            resume: '',
            resumeFile: '',
            statusId: ListId.talentsphere.resume.availableStatuId,
            completeAddress: '',
            linkedin: '',
            personalInformation: '',
            experienceTime: '',
            hardSkills: '',
            softSkills: '',
            professionalExperience: '',
            professionalExperiencePerHardSkill: '',
            validationQuestions: '',
            validationQuestionsAndAnswers: '',
            companyId: userInfo.user.companyId
        } as ResumeDTO);

        if(isOpenMaintenance){
            handleListGetAll(ListId.talentsphere.resume.jobTitleId, setJobTitleList);
            handleListGetAll(ListId.talentsphere.resume.seniorityId, setSeniorityList);
            handleUserGetAllAsync(setUserList);
            handleGetResumeByIdAsync();
        }
    }, [data, isOpenMaintenance]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleUserGetAllAsync = useCallback(async (setUserList: any) => {
        const userFilterDTO: UserFilterDTO = {
            companyId: '',
            peopleId: '',
            userName: '',
            isServiceAccount: false
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleGetResumeByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('talentsphere.resume', data.resumeId, setLoading, setResumeDTO, showMessage);
    }, [data]);

    const handlePrintClick = useCallback(async () => {
        window.print();
    }, [resumeDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'print', text: t('vacancy.dialogActions.print'), icon: 'print_outlined', iconColor: 'primary', setAction: handlePrintClick, isClose: false },
        { id: 'close', text: t('vacancy.dialogActions.close'), icon: 'close_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];
    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title={`${t('vacancy.customResume.dialogForm.title')} - ${resumeDTO?.code}`}
                icon='summarize_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'xl'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextFieldReadOnly
                                id={'name'}
                                label={t('vacancy.customResume.textFieldReadOnly.name')}
                                value={resumeDTO?.name}
                                hasDivider={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'jobTitleId'}
                                label={t('vacancy.customResume.textFieldReadOnly.jobTitle')}
                                value={String(jobTitleList.find(x => x.id === resumeDTO?.jobTitleId)?.value)}
                                hasDivider={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'seniorityId'}
                                label={t('vacancy.customResume.textFieldReadOnly.seniority')}
                                value={String(seniorityList.find(x => x.id === resumeDTO?.seniorityId)?.value)}
                                hasDivider={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'experienceTime'}
                                label={t('vacancy.customResume.textFieldReadOnly.experienceTime')}
                                value={resumeDTO?.experienceTime}
                                hasDivider={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldReadOnly
                                id={'hardSkills'}
                                label={t('vacancy.customResume.textFieldReadOnly.behavioralFeedback')}
                                value={resumeDTO?.hardSkills}
                                noWrap={true}
                                hasDivider={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldReadOnly
                                id={'hardSkills'}
                                label={t('vacancy.customResume.textFieldReadOnly.technicalFeedback')}
                                value={resumeDTO?.hardSkills}
                                noWrap={true}
                                hasDivider={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldReadOnly
                                id={'professionalExperience'}
                                label={t('vacancy.customResume.textFieldReadOnly.professionalExperience')}
                                value={resumeDTO?.professionalExperience}
                                noWrap={true}
                                hasDivider={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldReadOnly
                                id={'hardSkills'}
                                label={t('vacancy.customResume.textFieldReadOnly.hardSkills')}
                                value={resumeDTO?.hardSkills}
                                noWrap={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldReadOnly
                                id={'softSkills'}
                                label={t('vacancy.customResume.textFieldReadOnly.softSkills')}
                                value={resumeDTO?.softSkills}
                                noWrap={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldReadOnly
                                id={'coursesAndCertificates'}
                                label={t('vacancy.customResume.textFieldReadOnly.coursesAndCertificates')}
                                value={resumeDTO?.coursesAndCertificates}
                                noWrap={true}
                            />
                        </Grid>
                        {resumeDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label={t('vacancy.customResume.textFieldReadOnly.id')}
                                        value={resumeDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label={t('vacancy.customResume.textFieldReadOnly.createdBy')}
                                        value={userList.find(x => x.id === resumeDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label={t('vacancy.customResume.textFieldReadOnly.creationDate')}
                                        value={formatDateTime(String(resumeDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label={t('vacancy.customResume.textFieldReadOnly.updatedBy')}
                                        value={userList.find(x => x.id === resumeDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label={t('vacancy.customResume.textFieldReadOnly.updateDate')}
                                        value={formatDateTime(String(resumeDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};
